import { MediaParticipant } from "@dvrt/web-phone-client";
import * as React from "react";
import { useSelector } from "react-redux";
import { State } from "conference/types";
import { useDebugSettings } from "conference/tools";
import DebugInfoPanel from "../../../../DebugInfoPanel";
import ParticipantMenuButton from "./MenuButton";
import { AvatarContainer, SpeakingIndicatorOverlay, SpeakingIndicatorOverlaySpeaking } from "./styles";
import Caption from "./Caption";
import { useParticipantStatus } from "../../../participantStatus";
import { useParticipantDisplayName } from "../../../hooks";
import { getNameLetters } from "conference/components/meeting-page/room/tools";
import { Avatar, Box, styled } from "@mui/material";
import { useFillModeHandler } from "conference/components/meeting-page/room/participant-menu/hooks";
import { Video as VideoWidget } from "conference/components/meeting-page/widgets";

type Props = {
    participant: MediaParticipant;
    sx?: React.CSSProperties;
};

const AvatarTileContent = ({ participant }: { participant: MediaParticipant }) => {
    const displayName = useParticipantDisplayName(participant);
    return (
        <AvatarContainer>
            <Avatar style={{ fontSize: "1.1rem" }}>{getNameLetters(displayName)}</Avatar>
        </AvatarContainer>
    );
};

const ParticipantTileContainer = styled(Box)(({ theme }) => ({
    width: "100%",
    height: "100%",
    position: "relative",
    minHeight: 0,
    minWidth: 0,
    borderRadius: theme.shape.borderRadius,
    padding: 0,
    backgroundColor: theme.palette.background.default,
    overflow: "hidden",
    "&:hover .menuButton": {
        opacity: 0.9,
        transition: "opacity 0.2s",
    },
}));

export const Video = styled(VideoWidget)({
    width: "100%",
    height: "100%",
    verticalAlign: "bottom",
    objectFit: "cover",
});

const ParticipantTile = ({ participant, sx }: Props) => {
    const speakers = useSelector((state: State) => state.room.speakerList);
    const { isMuted } = useParticipantStatus(participant.cname);
    const speaking = speakers.includes(participant.cname) && !isMuted;
    const debugSettings = useDebugSettings();
    const { fitted } = useFillModeHandler(participant.cname, participant.screen);

    const track = participant.videoTrack;

    return (
        <ParticipantTileContainer sx={{ ...sx }}>
            {track ? (
                <Video key={track.id} track={track} style={{ objectFit: fitted ? "contain" : "cover" }} />
            ) : (
                <AvatarTileContent participant={participant} />
            )}

            <Caption participant={participant} />

            {track && debugSettings.enabled && debugSettings.showVideoInfo && <DebugInfoPanel track={track} />}

            <ParticipantMenuButton participant={participant} />

            {speaking ? <SpeakingIndicatorOverlaySpeaking /> : <SpeakingIndicatorOverlay />}
        </ParticipantTileContainer>
    );
};

export default ParticipantTile;
